import { SelectBoxUtils } from "./utilities.select-boxes"

/**
 * 'Make & Model' search filter controller.
 * 
 * @since 1.0.0
 * @package dealerkit-wp
 */
export class MakeAndModelController
{
    constructor(core, $container) {
        this.core = core

        this.utils = new SelectBoxUtils(this.core.window.document)

        this.previousState = {
            makeEnabled: false,
            budgetEnabled: false,
            modelEnabled: false,
        }

        this.state = {
            showCounts: true
        }

        this.setContainerElement($container)
    }

    setState(key, value) {
        if (typeof this.state[key] !== 'undefined') {
            this.state[key] = value
        }

        return this
    }

    setContainerElement(containerEl) {
        this.containerEl  = containerEl

        this.setControlElements()

        return this
    }

    setControlElements() {
        this.makeSelectorEl = this.containerEl.querySelector(
            'select.dealerkit-make-selector'
        )

        this.budgetTypeSelectorEl = this.containerEl.querySelector(
            'input.dealerkit-budget-type'
        )

        if (!this.budgetTypeSelectorEl) {
            this.budgetTypeSelectorEl = this.containerEl.querySelector(
                'select.dealerkit-budget-type'
            )
        }

        this.budgetSelectorEl = this.containerEl.querySelector(
            'select.dealerkit-budget-selector'
        )

        this.modelSelectorEl = this.containerEl.querySelector(
            'select.dealerkit-model-selector'
        )

        this.wireSelectionChangeEvents()
    }

    wireSelectionChangeEvents() {

        // Reset models on make change.
        this.makeSelectorEl.addEventListener('change', e => {
            this.modelSelectorEl.value = ''
        })

        // Update the results on make / model change.
        this.listenForChange(this.makeSelectorEl)
        this.listenForChange(this.budgetSelectorEl)
        this.listenForChange(this.modelSelectorEl)
        
        this.budgetTypeSelectorEl.addEventListener('change', () => {
            let budgetType

            if (this.budgetTypeSelectorEl.tagName.toLowerCase() === 'select') {
                budgetType = this.budgetTypeSelectorEl.value
            } else {
                budgetType = this.budgetTypeSelectorEl.checked ? 'monthly' : 'full'
            }

            this.populateBudget(budgetType)
        })
    }

    listenForChange(el) {
        el.addEventListener('change', e => {
            this.fireChanged(
                this.makeSelectorEl.value,
                this.modelSelectorEl.value,
                this.budgetTypeSelectorEl.tagName.toLowerCase() === 'select' ? 
                    this.budgetTypeSelectorEl.value : (this.budgetTypeSelectorEl.checked ? 'monthly' : 'full'),
                this.budgetSelectorEl.value,
            )
        })
    }

    fireChanged(make,model, budgetType, budget) {
        this.changeCallback.call(null, make, model, budgetType, budget)
    }

    change(callback) {
        this.changeCallback = callback
    }

    hideCounts() {
        this.state.showCounts = false
    }

    render(makes, models, budgetType, budget, searchParams) {
        // Set the makes.
        this.populateMakes(makes)

        // Set thep budget.
        this.budgets = budget
        this.populateBudget(budgetType)

        // Enable make selection
        this.enableMake()

        // Set the models if present
        if (models) {
            this.populateModels(models)
        } else {
            this.resetModels()
        }

        this.setSelectedOptions(searchParams)

        // Enable model selection
        if (models) {
            this.enableModel()
        } else {
            this.disableModel()
        }

        // Enable budget selection
        if (budget) {
            this.enableBudget()
        } else {
            this.disableBudget()
        }
    }

    enable() {
        if (this.previousState.makeEnabled) {
            this.makeSelectorEl.removeAttribute('disabled')
        } 

        if (this.previousState.modelEnabled) {
            this.modelSelectorEl.removeAttribute('disabled')
        }

        if (this.previousState.budgetEnabled) {
            this.budgetSelectorEl.removeAttribute('disabled')
        }
    }

    disable() {
        this.previousState = {
            makeEnabled: !this.makeSelectorEl.hasAttribute('disabled'),
            modelEnabled: !this.modelSelectorEl.hasAttribute('disabled'),
            budgetEnabled: !this.budgetSelectorEl.hasAttribute('disabled'),
        }

        this.makeSelectorEl.setAttribute('disabled', 'disabled')
        this.modelSelectorEl.setAttribute('disabled', 'disabled')
        this.budgetSelectorEl.setAttribute('disabled', 'disabled')
    }

    enableMake() {
        this.previousState.makeEnabled = true
        this.makeSelectorEl.removeAttribute('disabled')
    }

    disableMake() {
        this.previousState.makeEnabled = false
        this.makeSelectorEl.setAttribute('disabled', 'disabled')
    }

    enableModel() {
        this.previousState.modelEnabled = true
        this.modelSelectorEl.removeAttribute('disabled')
    }

    disableModel() {
        this.previousState.modelEnabled = false
        this.modelSelectorEl.setAttribute('disabled', 'disabled')
    }

    enableBudget() {
        this.previousState.budgetEnabled = true
        this.budgetSelectorEl.removeAttribute('disabled')
        this.budgetTypeSelectorEl.removeAttribute('disabled')
    }

    disableBudget() {
        this.previousState.budgetEnabled = false
        this.budgetSelectorEl.setAttribute('disabled', 'disabled')
        this.budgetTypeSelectorEl.setAttribute('disabled', 'disabled')
    }

    populateMakes(makes) {
        this.utils.removeOptions(this.makeSelectorEl)

        let placeholder = 'Any'
        if (this.makeSelectorEl.getAttribute('data-placeholder')) {
            placeholder = this.makeSelectorEl.getAttribute('data-placeholder')
        }

        this.utils.addOption(this.makeSelectorEl, placeholder, '')

        makes.forEach(item => {
            this.utils.addOption(
                this.makeSelectorEl, 
                item.label, 
                item.label, 
                this.state.showCounts ? item.count : null
            )
        })
    }

    populateBudget(budgetType) {
        if (!this.budgets[budgetType]) {
            return
        }

        this.utils.removeOptions(this.budgetSelectorEl)

        if (this.budgetTypeSelectorEl.tagName.toLowerCase() === 'select') {
            this.budgetTypeSelectorEl.value = budgetType
        } else {
            this.budgetTypeSelectorEl.checked === (budgetType === 'monthly')
        }

        let placeholder = 'Any'
        if (this.budgetSelectorEl.getAttribute('data-placeholder')) {
            placeholder = this.budgetSelectorEl.getAttribute('data-placeholder')
        }

        this.utils.addOption(this.budgetSelectorEl, placeholder, '')

        this.budgets[budgetType].ranges.to.forEach(item => {
            this.utils.addOption(
                this.budgetSelectorEl, 
                item.label, 
                item.id, 
                this.state.showCounts ? item.count : null
            )
        })
    }

    resetModels() {
        this.utils.removeOptions(this.modelSelectorEl)

        let placeholder = 'Any'
        if (this.modelSelectorEl.getAttribute('data-placeholder')) {
            placeholder = this.modelSelectorEl.getAttribute('data-placeholder')
        }

        this.utils.addOption(this.modelSelectorEl, placeholder, '')
    }

    populateModels(models) {
        this.resetModels()

        models.forEach(item => {
            this.utils.addOption(
                this.modelSelectorEl, 
                item.label, 
                item.label, 
                this.state.showCounts ? item.count : null
            )
        })
    }

    setSelectedOptions(searchParams) {
        if (searchParams.make) {
            this.utils.setOption(this.makeSelectorEl, searchParams.make)

            if (searchParams.model) {
                this.utils.setOption(this.modelSelectorEl, searchParams.model)
            }
        }

        if (searchParams.price_to) {
            this.utils.setOption(this.budgetSelectorEl, searchParams.price_to)
        }

        if (searchParams.price_search_type) {
            if (this.budgetTypeSelectorEl.tagName.toLowerCase() === 'select') {
                this.budgetTypeSelectorEl.value = searchParams.price_search_type
            } else {
                this.budgetTypeSelectorEl.checked = (searchParams.price_search_type === 'monthly')
            }
        }
    }
}